import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import TextInput from "../../Common/Textfield/Textfield";
import Button from "../../Common/Button/Button";
import "./Login.css";
import vybogTalLogo from "../../Assets/Images/VybogTAL-Logo.svg";
import vybogTalDarkLogo from "../../Assets/Images/VybogTAL-Logo-Dark.svg";
import sliderDots from "../../Assets/Images/slider-dots.png";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import SignUpIcon from "../../Assets/Images/Sign_Up_Icon.svg";
import successfullicon from "../../Assets/Images/resetpassword icon.svg";
import { post } from "../../helper/Api_helper";
import { DynamicContext } from "../../Context/DynamicContext";
import { VYLABS_BASE_URL, USER_RESET_PASSWORD } from "../../helper/url_helper";
import { TENANT_OTP_API_KEY } from "../../Tenant-Config/TenantConfig";

function ResetPassword() {
  const { isDarkMode } = useContext(DynamicContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const resetEmail = location.state?.email || "";

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleResetPassword = async (values) => {
    setIsLoading(true);
    try {
      await post(
        VYLABS_BASE_URL,
        USER_RESET_PASSWORD,
        {
          email: values.email,
          newPassword: values.password,
          otp: values.otp,
        },
        { 
          _skipAuth: true, 
          headers: {
            "X-API-Key": TENANT_OTP_API_KEY,
          },
        }
      );

      Toast("success", "Your password has been reset successfully.");
      navigate("/");
    } catch (error) {
      const responseData = error.response?.data;
      const message =
        responseData?.message ||
        responseData?.error ||
        (typeof responseData === "string" ? responseData : "");

      Toast(
        "error",
        message || "Invalid or expired code. Please request a new code.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: resetEmail,
      otp: "",
      password: "",
    },
    validateOnMount: true,
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Please enter your email."),
      otp: Yup.string()
        .matches(/^[0-9]{6}$/, "OTP must be exactly 6 digits")
        .required("Please enter the OTP."),
      password: Yup.string()
        .required("Please enter a new password.")
        .min(8, "Password must be at least 8 characters long."),
    }),
    onSubmit: handleResetPassword,
  });

  const hasValidationErrors =
    formik.submitCount > 0 && Object.keys(formik.errors).length > 0;

  const handleLogin = () => {
    navigate("/");
  };

  const handleForgot = () => {
    navigate("/forgot-password");
  };

  return (
    <div className="login-page">
      <div
        className={`login-main-container reset-password-main${
          hasValidationErrors ? " reset-password-main--with-errors" : ""
        }`}
      >
        <div className="login-header">
          <img
            alt="vylogo"
            src={isDarkMode ? vybogTalDarkLogo : vybogTalLogo}
            className="vylogo"
          />
        </div>
        <div className="login-container" style={{ marginTop: "20px" }}>
          <div className="forgot-content">
            <h1 className="forgot-title">Reset Password</h1>
            <p className="forgot-subtitle">
              Create a new strong password for your account.
            </p>
          </div>
          <div className="input-container" style={{ gap: "10px" }}>
            <TextInput
              type="email"
              label={"Email"}
              placeholder="Enter your email"
              labelStyle={{ fontWeight: 500 }}
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && formik.errors.email}
              errorMessage={formik.errors.email}
              required={true}
            />
            <TextInput
              type="text"
              label={"OTP"}
              placeholder="Enter 6-digit OTP"
              labelStyle={{ fontWeight: 500 }}
              name="otp"
              value={formik.values.otp}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.otp && formik.errors.otp}
              errorMessage={formik.errors.otp}
              required={true}
            />
            <TextInput
              type="password"
              label={"New Password"}
              placeholder="New Password"
              labelStyle={{ fontWeight: 500 }}
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && formik.errors.password}
              errorMessage={formik.errors.password}
              showPasswordToggle={true}
              required={true}
            />
            <div className="signup-options">
              <Button
                label={isLoading ? "Resetting..." : "Reset Password"}
                event={formik.handleSubmit}
                type="submit"
                backgroundColor="#6860EE"
                width="100%"
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="resend-email">
            <span
              className="signup-text-span"
              onClick={handleForgot}
              style={{ color: "#6866ee" }}
            >
              Resend Email
            </span>{" "}
          </div>
          <div className="forgot-signup-text" style={{ color: "#727277" }}>
            Remember your password?{" "}
            <span
              className="signup-text-span"
              onClick={handleLogin}
              style={{ color: "#6866ee" }}
            >
              Login
            </span>
            <span>
              <img
                className="forgot-signup-text"
                alt="anchor Icon"
                src={SignUpIcon}
                style={{ width: "20px", height: "20px", marginTop: "2px" }}
              ></img>
            </span>
          </div>
        </div>
      </div>
      {isDesktop && (
        <div className="signup-background">
          <img
            alt="background"
            src={successfullicon}
            className="bg-image"
          ></img>
          <div className="background-text">
            Specialized job-matching algorithms
          </div>
          <img alt="slider" src={sliderDots} className="slider-dots"></img>
        </div>
      )}
    </div>
  );
}

export default ResetPassword;
